import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { Play, Eye, Clock, Activity, DollarSign, LucideIcon } from 'lucide-react';
import { MetricCard } from './MetricCard';
import { useDashboardMetrics, calcPlayRate } from '@/hooks/useDashboardMetrics';
import { useCustomAuth } from '@/contexts/CustomAuthContext';

type HourlyBucket = { hour: number; plays: number; views: number; conversions: number };

type MetricCardSpec = {
  icon: LucideIcon;
  title: string;
  value: number | string;
  subtitle: string;
  formatValue?: (v: number) => string;
  sparkData: number[];
  metricKey: string;
};

function buildSparkFromHourly(hourly: HourlyBucket[], field: 'plays' | 'views' | 'conversions'): number[] {
  if (!hourly || hourly.length === 0) return [];
  return hourly.map((h) => h[field]);
}

// Play rate por hora (plays / views únicas), na mesma escala 0-100 do card
function buildPlayRateSpark(hourly: HourlyBucket[]): number[] {
  if (!hourly || hourly.length === 0) return [];
  return hourly.map((h) => calcPlayRate(h.plays, h.views));
}

function buildPeakSpark(hourly: HourlyBucket[]): number[] {
  if (!hourly || hourly.length === 0) return [];
  return hourly.map((h) => h.views);
}

export function MetricsPanel() {
  useLanguage();
  const { user } = useCustomAuth();
  const { data: metrics, isLoading } = useDashboardMetrics(user?.id);

  const hourly = metrics?.hourly ?? [];

  const cards: MetricCardSpec[] = [
    {
      icon: Play,
      title: translateInterfaceText("Plays hoje"),
      value: metrics?.plays_today ?? 0,
      subtitle: translateInterfaceText("Total do dia"),
      sparkData: buildSparkFromHourly(hourly, 'plays'),
      metricKey: 'plays',
    },
    {
      icon: Activity,
      title: translateInterfaceText("Play Rate hoje"),
      value: metrics?.play_rate_today ?? 0,
      subtitle: translateInterfaceText("Conversão de abertura"),
      formatValue: (v: number) => `${v}%`,
      sparkData: buildPlayRateSpark(hourly),
      metricKey: 'playRate',
    },
    {
      icon: Eye,
      title: translateInterfaceText("Visualizações hoje"),
      value: metrics?.views_today ?? 0,
      subtitle: translateInterfaceText("Todos os vídeos"),
      sparkData: buildSparkFromHourly(hourly, 'views'),
      metricKey: 'views',
    },
    {
      icon: Clock,
      title: translateInterfaceText("Retenção média hoje"),
      value: metrics?.avg_retention_today ?? 0,
      subtitle: translateInterfaceText("Tempo médio assistido"),
      formatValue: (v: number) => `${v}%`,
      // Sem curva horária: o back ainda não devolve retenção por hora e a série
      // de play rate mostraria o mesmo gráfico do card acima. Card fica sem clique.
      sparkData: [],
      metricKey: 'retention',
    },
    {
      icon: Activity,
      title: translateInterfaceText("Pico de audiência"),
      value: metrics?.peak_hour_today ?? '—',
      subtitle: translateInterfaceText("Horário com mais espectadores"),
      sparkData: buildPeakSpark(hourly),
      metricKey: 'peak',
    },
    {
      icon: DollarSign,
      title: translateInterfaceText("Conversões hoje"),
      value: metrics?.conversions_today ?? 0,
      subtitle: translateInterfaceText("Últimas 24h"),
      sparkData: buildSparkFromHourly(hourly, 'conversions'),
      metricKey: 'conversions',
    },
  ];

  return (
    <div className="w-full relative">
      <div className="grid grid-cols-1 min-[500px]:grid-cols-2 min-[900px]:grid-cols-3 min-[1300px]:grid-cols-6 gap-3 min-[1400px]:gap-[12px] w-full">
        {cards.map((card, i) => (
          <MetricCard
            key={card.title}
            icon={card.icon}
            title={card.title}
            value={isLoading ? 0 : card.value}
            subtitle={isLoading ? '' : card.subtitle}
            index={i}
            isLoading={isLoading}
            formatValue={card.formatValue}
            sparkData={card.sparkData}
            metricKey={card.metricKey}
          />
        ))}
      </div>
    </div>
  );
}
